import { BehaviorSubject, from, Observable, of, Subscription, tap } from 'rxjs';
import { catchError, filter, finalize, mergeMap, take } from 'rxjs/operators';
import { Injectable, OnDestroy } from '@angular/core';
import { ImgAnnotation } from '../model/img-annotation';
import { ImgDbService } from './img-db.service';
import { Action } from '../model/action';
import { ActionType } from '../model/action-type.enum';
import { v4 as uuidv4 } from 'uuid';
import { MessageData, MessageType } from '../../message/models/message';
import { MessageService } from '../../message/services/message.service';

const PREFETCH_CONCURRENCY = 2;
/**
 * Hard upper bound on the number of (imageDbId, imgId) entries kept in the
 * SWR cache. The cache uses Map insertion order as a poor-man's LRU - on
 * read the entry is moved to the back, on write the oldest entry is dropped
 * once we exceed this bound. Sized for ~200 images of ~50 annotations each
 * (~10k DOMRects), well within memory budget on modest client hardware.
 */
const MAX_CACHE_ENTRIES = 200;

@Injectable()
export class AnnotationStateService implements OnDestroy {
    /**
     * Annotations that are displayed in the current image.
     * @usageNotes
     * Never Add or Delete directly from that BehaviorSubject.
     * Always use the provided Service Methods (e.g. addAnnotation, removeAnnotation or handleAction).
     * @type {BehaviorSubject<ImgAnnotation[]>}
     */
    public $annotations: BehaviorSubject<ImgAnnotation[]> = new BehaviorSubject(
        []
    );
    /**
     * True while {@link fetchAnnotations} is fetching annotations for the
     * currently selected image and no cached/server result has been emitted yet.
     * Components should bind their loading spinner to this signal instead of
     * deriving it from $annotations / selectedImage$ to avoid a race when the
     * cache emits synchronously before the selected image change propagates.
     */
    public $loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
        false
    );
    private actions: Action[] = [];
    private actionStateIndex: number = -1;
    private imageDbId: string;
    private maxActionsLimit = 50;
    private selectedImgId: string;
    private commands: Map<string, Action[]> = new Map<string, Action[]>();
    /**
     * In-memory cache of annotations keyed by `${imageDbId}::${imgId}`.
     * Stored values are deep copies so subscribers can mutate emitted
     * annotations without poisoning the cache.
     */
    private annotationCache: Map<string, ImgAnnotation[]> = new Map();
    /**
     * Refcount of in-flight saves keyed by `${imageDbId}::${imgId}`. A
     * `Set<string>` is not enough: if two saves for the same image overlap,
     * the first response's `delete` would clear the flag while the second
     * save is still in flight, letting an SWR GET slip through and race
     * the second save. We treat the key as in-flight as long as the count
     * is greater than zero. See {@link noteSaveStarted} / {@link noteSaveFinished}.
     */
    private inFlightSaveCount: Map<string, number> = new Map<string, number>();
    /**
     * Monotonically incremented per cache key on every save dispatch. The
     * token captured at dispatch is checked when the response arrives - if a
     * newer save has started for the same image since, the older response
     * must not write to the cache (otherwise out-of-order arrivals would
     * let stale data overwrite fresh data).
     */
    private saveTokens: Map<string, number> = new Map<string, number>();
    /**
     * Monotonically incremented for every {@link fetchAnnotations} call.
     * The token captured at call time is checked when the response arrives
     * (and in {@link finalize}); a stale token means the user has since
     * navigated to a different image (or back-and-forth) and the response
     * must not touch UI state ({@link $annotations}, {@link $loading}).
     * The cache is still refreshed because the network work has been done.
     */
    private fetchToken: number = 0;
    /**
     * Outstanding {@link fetchAnnotations} / {@link prefetchAnnotations}
     * subscriptions. Tracked so {@link clearCache} can cancel any HTTP
     * responses still in flight when the host component is destroyed -
     * otherwise a late-arriving response would call {@link setCache} after
     * the cache has been wiped, leaving stale entries behind across remounts.
     */
    private outstandingFetches: Set<Subscription> = new Set<Subscription>();
    // TODO: verkapsel das, sodass selectedAnnotation nicht die selbe Object Instanz wie in $annnotation sein muss
    selectedAnnotation: ImgAnnotation = null;

    /**
     * If True does not update selectedAnnotation on BE Annotation Update. (Example: Don't interrupt Annotation moving)
     * @type {boolean}
     */
    bboxCurrentlyModifiable: boolean = false;

    constructor(
        private imgDbService: ImgDbService,
        private messageService: MessageService
    ) {}

    /**
     * Fetch annotations for an image with stale-while-revalidate semantics.
     *
     * Behaviour:
     * - If the (imageDbId, imgId) is in the cache, the cached annotations
     *   are emitted synchronously and `$loading` is flipped false in the same
     *   tick, so the caller never sees a spinner for cached images.
     * - The HTTP request is always issued. When it returns:
     *   - The cache is refreshed with the server result (even if the user
     *     has navigated away in the meantime - the network work is done).
     *   - If the {@link fetchToken} captured at call time is no longer
     *     current (rapid A->B->A navigation, or A->B), UI state is left
     *     alone and the response is dropped at the UI layer.
     *   - If pending local edits exist (commands.size > 0) the response is
     *     merged via {@link mergeCommandsAndAnnotations} so local edits
     *     survive; the merged view is re-cached. The parallel-editing toast
     *     is suppressed only for diffs the user has pending edits on -
     *     genuine third-party edits still surface.
     *   - Otherwise, fresh annotations are emitted only when they actually
     *     differ from what was last shown (avoids a redundant render).
     * - `neighbourIds` triggers a silent prefetch (no $annotations / $loading
     *   side effects) so navigating to nearby images becomes instant.
     * - On HTTP error, $loading is unstuck via the finalize block.
     */
    fetchAnnotations(
        imageDbId: string,
        imgId: string,
        neighbourIds?: string[]
    ) {
        // Free entries from the previous DB - they can't be revalidated from
        // the current view, and keeping them around just chews memory until
        // the LRU bound kicks in.
        if (this.imageDbId && this.imageDbId !== imageDbId) {
            this.clearCacheForImageDb(this.imageDbId);
        }

        this.selectedImgId = imgId;
        this.imageDbId = imageDbId;
        this.commands.clear();
        this.resetActions();
        this.$loading.next(true);

        const dispatchedToken = ++this.fetchToken;
        const cached = this.getFromCache(imageDbId, imgId);
        const cacheHit = !!cached;
        if (cacheHit) {
            this.$annotations.next(cached);
            this.$loading.next(false);
        }

        // Schedule the neighbourhood prefetch up front so both the
        // pending-save early-return path and the normal SWR path use the
        // same call site. prefetchAnnotations already de-duplicates against
        // already-cached and in-flight-save ids.
        if (neighbourIds && neighbourIds.length > 0) {
            this.prefetchAnnotations(imageDbId, neighbourIds);
        }

        // If a save HTTP for this image is still in flight, the server has
        // not applied our local edits yet. Issuing a GET now would race the
        // save and could overwrite the cache with the stale pre-edit state,
        // producing a visible flicker (new -> old -> new) when the user
        // navigates back to a freshly edited image. Trust the cache; the
        // save response will refresh it and re-emit annotations through the
        // saveCommands tap when it returns.
        const hasPendingSave = this.isSaveInFlight(
            this.cacheKey(imageDbId, imgId)
        );
        if (cacheHit && hasPendingSave) {
            return;
        }

        const fetchAnnotationsSubscription = this.imgDbService
            .getAllAnnotationForImage(imageDbId, imgId)
            .pipe(
                catchError(() => of(null)),
                finalize(() => {
                    // Unstick the spinner on error/teardown, but only if
                    // we're still the most recent fetch and we didn't
                    // already serve from cache (in which case $loading was
                    // flipped synchronously above).
                    if (
                        !cacheHit &&
                        this.fetchToken === dispatchedToken &&
                        this.$loading.getValue()
                    ) {
                        this.$loading.next(false);
                    }
                })
            )
            .subscribe((freshAnnotations: ImgAnnotation[] | null) => {
                if (freshAnnotations === null) {
                    return;
                }
                // Always refresh the cache, even if a newer fetch superseded
                // this one - the work is done, no reason to discard it.
                this.setCache(imageDbId, imgId, freshAnnotations);

                // Stale guard: another fetchAnnotations() call has happened
                // since we issued this request. Don't touch UI state, the
                // newer call owns it now.
                if (this.fetchToken !== dispatchedToken) {
                    return;
                }

                if (this.commands.size > 0) {
                    // Local edits pending - protect them by merging instead
                    // of overwriting. The merge step transparently rewrites
                    // any annotation the user has pending commands on with
                    // the user's local state, so the parallel-editing toast
                    // only fires for ids the user has not touched.
                    this.mergeCommandsAndAnnotations(freshAnnotations);
                    this.setCache(
                        imageDbId,
                        imgId,
                        this.$annotations.getValue()
                    );
                } else if (!cacheHit) {
                    this.$annotations.next(freshAnnotations);
                } else if (
                    this.annotationsDiffer(
                        this.$annotations.getValue(),
                        freshAnnotations
                    )
                ) {
                    this.$annotations.next(freshAnnotations);
                }

                if (!cacheHit) {
                    this.$loading.next(false);
                }
            });
        this.trackOutstandingFetch(fetchAnnotationsSubscription);
    }

    /**
     * Track an outstanding HTTP subscription so {@link clearCache} can cancel
     * it on host destruction. Subscriptions self-evict once they complete,
     * which keeps the set bounded to genuinely in-flight work.
     */
    private trackOutstandingFetch(
        fetchAnnotationsSubscription: Subscription
    ): void {
        if (fetchAnnotationsSubscription.closed) {
            return;
        }
        this.outstandingFetches.add(fetchAnnotationsSubscription);
        fetchAnnotationsSubscription.add(() =>
            this.outstandingFetches.delete(fetchAnnotationsSubscription)
        );
    }

    /**
     * Background-prefetch annotations for the given image ids without ever
     * touching {@link $annotations} or {@link $loading}. Used for the
     * sliding-window prefetch around the currently selected image.
     */
    prefetchAnnotations(imageDbId: string, imgIds: string[]) {
        const seenIds = new Set<string>();
        const idsToFetch = imgIds.filter((id) => {
            if (!id || seenIds.has(id)) return false;
            seenIds.add(id);
            const key = this.cacheKey(imageDbId, id);
            // Skip ids already cached. Also skip ids with a save HTTP in
            // flight: a GET now would race the PUT and we'd cache the
            // pre-edit response, re-introducing exactly the flicker that
            // the in-flight tracking was added to prevent.
            if (this.annotationCache.has(key)) return false;
            if (this.isSaveInFlight(key)) return false;
            return true;
        });
        if (idsToFetch.length === 0) {
            return;
        }
        const prefetchAnnotationSubscription = from(idsToFetch)
            .pipe(
                mergeMap(
                    (id) =>
                        this.imgDbService
                            .getAllAnnotationForImage(imageDbId, id)
                            .pipe(
                                tap((annotations: ImgAnnotation[]) =>
                                    this.setCache(imageDbId, id, annotations)
                                ),
                                catchError(() => of(null))
                            ),
                    PREFETCH_CONCURRENCY
                )
            )
            .subscribe();
        this.trackOutstandingFetch(prefetchAnnotationSubscription);
    }

    /**
     * Drop every cached entry that belongs to the given image database. Used
     * when bulk mutations (category rename, mass delete) make per-image cache
     * entries inconsistent with what the user is seeing.
     */
    public clearCacheForImageDb(imageDbId: string): void {
        const prefix = `${imageDbId}::`;
        for (const key of Array.from(this.annotationCache.keys())) {
            if (key.startsWith(prefix)) {
                this.annotationCache.delete(key);
            }
        }
    }

    /**
     * Drop the entire SWR cache. Called by {@link ngOnDestroy} and exposed
     * so explicit cache invalidation paths (e.g. tests, or callers that want
     * to force a full refetch) can reset state without tearing down the
     * service. Cancels any in-flight fetches/prefetches so a late-arriving
     * response can't repopulate the cache after it has been wiped.
     */
    public clearCache(): void {
        this.annotationCache.clear();
        this.inFlightSaveCount.clear();
        this.saveTokens.clear();
        // Snapshot before iterating: each unsubscribe synchronously fires a
        // teardown that deletes from `outstandingFetches`, which would
        // otherwise mutate the set we're walking.
        const snapshot = Array.from(this.outstandingFetches);
        this.outstandingFetches.clear();
        for (const fetchAnnotationsSubscription of snapshot) {
            fetchAnnotationsSubscription.unsubscribe();
        }
    }

    /**
     * Angular calls this when the host {@link ImgDbComponent} is destroyed
     * (the service is provided at the component level, so its lifetime is
     * tied to the component). Forwarding to {@link clearCache} ensures all
     * in-flight HTTP work is cancelled and no orphaned state lingers.
     */
    ngOnDestroy(): void {
        this.clearCache();
    }

    private isSaveInFlight(key: string): boolean {
        return (this.inFlightSaveCount.get(key) ?? 0) > 0;
    }

    private noteSaveStarted(key: string): void {
        this.inFlightSaveCount.set(
            key,
            (this.inFlightSaveCount.get(key) ?? 0) + 1
        );
    }

    private noteSaveFinished(key: string): void {
        const next = (this.inFlightSaveCount.get(key) ?? 1) - 1;
        if (next <= 0) {
            this.inFlightSaveCount.delete(key);
        } else {
            this.inFlightSaveCount.set(key, next);
        }
    }

    private cacheKey(imageDbId: string, imgId: string): string {
        return `${imageDbId}::${imgId}`;
    }

    private setCache(
        imageDbId: string,
        imgId: string,
        annotations: ImgAnnotation[]
    ): void {
        const annotationCopies = annotations.map((annotation) =>
            this.cloneAnnotationForCache(annotation)
        );
        const key = this.cacheKey(imageDbId, imgId);
        // Move the key to the end of insertion order so it counts as
        // most-recently-used for LRU eviction below.
        if (this.annotationCache.has(key)) {
            this.annotationCache.delete(key);
        }
        this.annotationCache.set(key, annotationCopies);
        // Map iteration order is insertion order; the first key is therefore
        // the LRU candidate (reads also re-insert via getFromCache).
        for (const oldestKey of this.annotationCache.keys()) {
            if (this.annotationCache.size <= MAX_CACHE_ENTRIES) break;
            this.annotationCache.delete(oldestKey);
        }
    }

    private getFromCache(
        imageDbId: string,
        imgId: string
    ): ImgAnnotation[] | undefined {
        const key = this.cacheKey(imageDbId, imgId);
        const cachedAnnotations = this.annotationCache.get(key);
        if (!cachedAnnotations) {
            return undefined;
        }
        // Refresh LRU position - touched entries should not be the next ones
        // evicted.
        this.annotationCache.delete(key);
        this.annotationCache.set(key, cachedAnnotations);
        return cachedAnnotations.map((annotation) =>
            this.cloneAnnotationForCache(annotation)
        );
    }

    /**
     * Variant of {@link deepCopyImgAnnotation} that tolerates null/undefined
     * bbox and polygon, since cached payloads can come straight from the
     * backend before any UI normalisation.
     */
    private cloneAnnotationForCache(annotation: ImgAnnotation): ImgAnnotation {
        const copy = new ImgAnnotation(
            annotation.id,
            annotation.imageId,
            annotation.categoryId,
            annotation.bbox?.x,
            annotation.bbox?.y,
            annotation.bbox?.width,
            annotation.bbox?.height,
            annotation.polygon ? [...annotation.polygon] : annotation.polygon
        );
        if (annotation.bbox === null) {
            copy.bbox = null;
        }
        copy.active = annotation.active;
        return copy;
    }

    private annotationsDiffer(
        current: ImgAnnotation[],
        fresh: ImgAnnotation[]
    ): boolean {
        if (current.length !== fresh.length) return true;
        const freshById = new Map<string, ImgAnnotation>(
            fresh.map((annotation) => [annotation.id, annotation])
        );
        for (const currentAnn of current) {
            const freshAnn = freshById.get(currentAnn.id);
            if (!freshAnn) return true;
            if (currentAnn.categoryId !== freshAnn.categoryId) return true;
            const currentBbox = currentAnn.bbox;
            const freshBbox = freshAnn.bbox;
            if (!!currentBbox !== !!freshBbox) return true;
            if (currentBbox && freshBbox) {
                if (Math.round(currentBbox.x) !== Math.round(freshBbox.x))
                    return true;
                if (Math.round(currentBbox.y) !== Math.round(freshBbox.y))
                    return true;
                if (
                    Math.round(currentBbox.width) !==
                    Math.round(freshBbox.width)
                )
                    return true;
                if (
                    Math.round(currentBbox.height) !==
                    Math.round(freshBbox.height)
                )
                    return true;
            }
            const currentPolygon = currentAnn.polygon ?? [];
            const freshPolygon = freshAnn.polygon ?? [];
            if (currentPolygon.length !== freshPolygon.length) return true;
            for (let i = 0; i < currentPolygon.length; i++) {
                if (
                    Math.round(currentPolygon[i]) !==
                    Math.round(freshPolygon[i])
                )
                    return true;
            }
        }
        return false;
    }

    renameAnnotations(oldId: string, newId: string) {
        this.$annotations.value.forEach((annotation: ImgAnnotation) => {
            if (annotation.categoryId === oldId) {
                annotation.categoryId = newId;
            }
        });
        if (this.imageDbId) {
            this.clearCacheForImageDb(this.imageDbId);
        }
    }

    hasUndo() {
        return (
            this.actions.length > 0 &&
            this.actionStateIndex !== this.actions.length - 1 &&
            this.actions[this.actionStateIndex + 1].annotation.id !== undefined
        );
    }

    hasRedo() {
        return (
            this.actions.length > 0 &&
            this.actionStateIndex >= 0 &&
            this.actions[this.actionStateIndex].annotation.id !== undefined
        );
    }

    undo() {
        const action = this.actions[++this.actionStateIndex];
        switch (action.type) {
            case ActionType.Add:
                this.removeAnnotation(action.annotation.id, action);
                break;
            case ActionType.Remove:
                this.addAnnotation(action.annotation, action);
                break;
            case ActionType.Update:
                if (!action.originalAnnotation) {
                    throw new Error(
                        `original Annotation of Action: ${action} not present. Cant undo Move Action`
                    );
                }
                this.updateAnnotation(
                    action.originalAnnotation,
                    action.annotation,
                    false
                );
                break;
        }
    }

    redo() {
        const action = this.actions[this.actionStateIndex--];
        switch (action.type) {
            case ActionType.Remove:
                this.removeAnnotation(action.annotation.id, action);
                break;
            case ActionType.Add:
                this.addAnnotation(action.annotation, action);
                break;
            case ActionType.Update:
                this.updateAnnotation(
                    action.annotation,
                    action.originalAnnotation,
                    false
                );
                break;
        }
    }

    resetActions() {
        this.actionStateIndex = -1;
        this.actions = [];
    }

    addAnnotation(newAnnotation: ImgAnnotation, action?: Action) {
        if (!newAnnotation.id) {
            newAnnotation.id = uuidv4();
        }
        const newAction: Action = {
            type: ActionType.Add,
            annotation: newAnnotation,
        };
        this.handleAction(newAction);
        const copiedAnnotation = this.deepCopyImgAnnotation(newAnnotation);
        this.$annotations.next([
            ...this.$annotations.getValue(),
            copiedAnnotation,
        ]);

        if (!action) {
            this.addAction(newAction);
        }
        this.cacheCurrentAnnotations();
    }

    updateAnnotation(
        updatedAnnotation: ImgAnnotation,
        oldAnnotation: ImgAnnotation,
        addAction = true
    ) {
        const newAction: Action = {
            type: ActionType.Update,
            annotation: updatedAnnotation,
            originalAnnotation: oldAnnotation,
        };
        this.handleAction(newAction);
        const updatedAnnotations = this.patchAnnotation(updatedAnnotation);
        // For Future: If something wierd happens while moving, then also make sure selectedAnnotation won't get changed with this next
        this.$annotations.next(updatedAnnotations);
        if (addAction) {
            this.addAction(newAction);
        }
        this.cacheCurrentAnnotations();
    }

    private patchAnnotation(updatedAnnotation: ImgAnnotation): ImgAnnotation[] {
        return this.$annotations.value.map((annotation: ImgAnnotation) => {
            if (annotation.id === updatedAnnotation.id) {
                // Dont copy by reference
                annotation.bbox.x = updatedAnnotation.bbox.x;
                annotation.bbox.y = updatedAnnotation.bbox.y;
                annotation.bbox.width = updatedAnnotation.bbox.width;
                annotation.bbox.height = updatedAnnotation.bbox.height;
                annotation.polygon = updatedAnnotation.polygon;
                annotation.categoryId = updatedAnnotation.categoryId;
            }
            return annotation;
        });
    }

    private handleAction(action: Action) {
        const actionToAdd: Action = this.deepCopyAction(action);
        actionToAdd.annotation.active = false;
        let fromMap = this.commands.get(actionToAdd.annotation.id);
        if (!fromMap) {
            this.commands.set(actionToAdd.annotation.id, [actionToAdd]);
        } else {
            this.commands.get(actionToAdd.annotation.id).push(actionToAdd);
        }
    }

    removeAllAnnotationsForCategory(
        categoryId: string,
        enableUndoRedo: boolean
    ) {
        let newAnnotations = this.$annotations.value.filter(
            (annotation: ImgAnnotation) => {
                const toBeRemoved = annotation.categoryId === categoryId;
                if (toBeRemoved) {
                    const removeAction = {
                        type: ActionType.Remove,
                        annotation: annotation,
                    } as Action;
                    this.handleAction(removeAction);
                    if (enableUndoRedo) {
                        this.addAction(removeAction);
                    }
                }
                return !toBeRemoved;
            }
        );
        this.$annotations.next(newAnnotations);
        if (this.imageDbId) {
            this.clearCacheForImageDb(this.imageDbId);
        }
    }

    removeAnnotation(id: string, action?: Action) {
        const index = this.getAnnotationIndex(this.$annotations.getValue(), id);

        if (index === -1) {
            return;
        }
        const removed: ImgAnnotation = this.$annotations
            .getValue()
            .splice(index, 1)[0];

        this.handleAction({
            type: ActionType.Remove,
            annotation: removed,
        } as Action);

        if (!action) {
            this.addAction({
                type: ActionType.Remove,
                annotation: removed,
            } as Action);
        }
        if (this.selectedAnnotation?.id === id) {
            this.selectedAnnotation = null;
        }
        this.$annotations.next(this.$annotations.getValue());
        this.cacheCurrentAnnotations();
    }

    /**
     * Write the current $annotations value into the cache entry for the
     * currently selected image. Called after every local mutation so a user
     * who navigates away and comes back before autosave fires still sees
     * their unsaved edits (cache hit on return matches what they last saw).
     */
    private cacheCurrentAnnotations(): void {
        if (!this.imageDbId || !this.selectedImgId) {
            return;
        }
        this.setCache(
            this.imageDbId,
            this.selectedImgId,
            this.$annotations.getValue()
        );
    }

    selectAnnotation(id: string) {
        const annotationsToEmit: ImgAnnotation[] = [];
        let selectedAnnotation: ImgAnnotation = null;
        this.$annotations
            .pipe(take(1))
            .subscribe((annotations: ImgAnnotation[]) => {
                if (!annotations?.some((annotation) => annotation?.id === id)) {
                    return;
                }
                annotations.forEach((annotation: ImgAnnotation) => {
                    if (annotation.id === id) {
                        if (annotation.active) {
                            this.selectedAnnotation = null;
                        } else {
                            this.selectedAnnotation = annotation;
                        }
                        annotation.active = !annotation.active;
                        selectedAnnotation = annotation;
                    } else {
                        annotation.active = false;
                        annotationsToEmit.push(annotation);
                    }
                });
                annotationsToEmit.push(selectedAnnotation);
                this.$annotations.next(annotationsToEmit);
            });
    }

    saveCommands(loadAfterSave: boolean, selectedImageId: string) {
        if (selectedImageId && this.commands.size > 0) {
            return this.prepareAndSendCommands().pipe(
                tap((imgAnnotations: ImgAnnotation[]) => {
                    if (
                        loadAfterSave &&
                        selectedImageId === this.selectedImgId
                    ) {
                        // prepare for synchronized save and load of annotations
                        this.mergeCommandsAndAnnotations(imgAnnotations);
                        // Cache the post-merge view (not the raw response) so
                        // edits the user made while the save was in flight
                        // survive a later cache hit for the same image.
                        if (this.imageDbId && this.selectedImgId) {
                            this.setCache(
                                this.imageDbId,
                                this.selectedImgId,
                                this.$annotations.getValue()
                            );
                        }
                    }
                })
            );
        }
        return of(null);
    }

    saveCommandsBeforeLogout(): Observable<ImgAnnotation[]> {
        if (this.commands.size > 0) {
            return this.prepareAndSendCommands();
        } else return of(null);
    }

    private prepareAndSendCommands(): Observable<ImgAnnotation[]> {
        this.cleanUpCommands();
        const clonedActions = [...this.commands.values()].flat().slice();
        this.commands.clear();
        // Capture the targeted image at the moment of dispatch so a later
        // selection change can't make us cache the response under the wrong key.
        const dispatchedDbId = this.imageDbId;
        const dispatchedImgId = this.selectedImgId;
        const inflightKey =
            dispatchedDbId && dispatchedImgId
                ? this.cacheKey(dispatchedDbId, dispatchedImgId)
                : null;
        // Bump the per-key save token. Concurrent saves for the same image
        // share an in-flight refcount but each gets its own token: only the
        // newest token's response is allowed to write the cache, so an
        // out-of-order arrival from an older save cannot overwrite fresher
        // server state.
        let dispatchedSaveToken = 0;
        if (inflightKey) {
            this.noteSaveStarted(inflightKey);
            dispatchedSaveToken = (this.saveTokens.get(inflightKey) ?? 0) + 1;
            this.saveTokens.set(inflightKey, dispatchedSaveToken);
        }
        const isLatestSave = (): boolean =>
            !inflightKey ||
            this.saveTokens.get(inflightKey) === dispatchedSaveToken;
        return this.imgDbService
            .saveAnnotationCommands(
                this.imageDbId,
                this.selectedImgId,
                clonedActions
            )
            .pipe(
                // Drop the emission entirely if a newer save for the same
                // image has already been dispatched. Filtering here (instead
                // of just guarding the inner setCache) is essential because
                // the outer saveCommands wrapper has its own tap that
                // re-merges the response into $annotations and re-caches the
                // result - if we let a stale response through to that tap,
                // the merge would overwrite the cache with the older state.
                filter(() => isLatestSave()),
                tap((imgAnnotations: ImgAnnotation[]) => {
                    if (inflightKey) {
                        this.setCache(
                            dispatchedDbId,
                            dispatchedImgId,
                            imgAnnotations
                        );
                    }
                }),
                finalize(() => {
                    if (inflightKey) {
                        this.noteSaveFinished(inflightKey);
                    }
                })
            );
    }

    private cleanUpCommands(): void {
        /**
         * Cleans Up unnecessary commands and sets correct .
         * For Example [(AnnotationID: 1, Add), (AnnotationID:1, Remove)] is the same as not having
         * any commands for that action.
         * @returns void, commands map is changed
         */
        const newCommands: Map<string, Action[]> = new Map<string, Action[]>();
        this.commands.forEach((value, key) => {
            let resultAction: Action = this.cleanUpAction(value);
            if (resultAction) {
                newCommands.set(key, [resultAction]);
            }
        });
        this.commands = newCommands;
    }

    private cleanUpAction(actions: Action[]): Action {
        /**
         * cleans Up Single Action
         * @param(actions) All Actions, that are recorded in the commands for one ImgAnnotation
         * @returns the resulting Action
         */
        const lastAction = actions[actions.length - 1];
        const firstAction = actions[0];
        switch (firstAction.type) {
            // Wenn die erste Action Update war, kann die resultierende Action nur Remove oder Update sein (fürs Backend)
            case ActionType.Update:
            // Wenn die erste Action Remove war, kann die resultierende Action nur Remove oder Update sein (fürs Backend)
            case ActionType.Remove:
                if (lastAction.type === ActionType.Add) {
                    return {
                        type: ActionType.Update,
                        annotation: lastAction.annotation,
                        originalAnnotation: null,
                    } as Action;
                }
                return lastAction;

            //Wenn die erste Action Add war, kann die resultierende Action nur Add oder Remove sein (fürs Backend)
            case ActionType.Add:
                if (lastAction.type !== ActionType.Remove) {
                    return {
                        type: ActionType.Add,
                        annotation: lastAction.annotation,
                        originalAnnotation: null,
                    } as Action;
                }
                // [Add, ... ,Remove] is the same as no annotation
                return null;
        }
    }

    private mergeCommandsAndAnnotations(
        visualAnnotations: ImgAnnotation[]
    ): void {
        const selectedAnnotationActions: Action[] =
            this.dontInterruptBboxModifying(visualAnnotations);
        this.commands.forEach((value, _) => {
            this.updateVisualAnnotationsAndCheckIfCommandAlreadyExecuted(
                visualAnnotations,
                // Last Action is the action that counts for now
                value[value.length - 1]
            );
        });
        if (selectedAnnotationActions) {
            this.commands.set(
                this.selectedAnnotation.id,
                selectedAnnotationActions
            );
        }
        this.restoreActiveFlag(visualAnnotations);

        // Note: we deliberately don't filter the toast by `commands.keys()`.
        // Step 2 above (updateVisualAnnotationsAndCheckIfCommandAlreadyExecuted)
        // already overwrites visualAnnotations[id] with the user's pending
        // state for any id with pending commands, so the diff inside
        // findUnexpectedChangesFromCloud naturally returns no entry for those
        // ids. Adding an explicit "ids in commands" filter on top would
        // also suppress genuine conflicts on those same ids (e.g. when a
        // third party edits the same annotation the user is editing).
        this.findUnexpectedChangesFromCloud(visualAnnotations);
        this.$annotations.next(visualAnnotations);
    }

    private findUnexpectedChangesFromCloud(visualAnnotations: ImgAnnotation[]) {
        // Do Toast Messages
        const backendMap: Map<string, ImgAnnotation> = new Map(
            visualAnnotations.map((annotation) => [annotation.id, annotation])
        );
        const localMap: Map<string, ImgAnnotation> = new Map(
            this.$annotations.value.map((annotation) => [
                annotation.id,
                annotation,
            ])
        );
        // Actions that were applied from the Backend, but weren't present locally (Could be useful for debugging)
        let newFromParallelEditing: Action[] = [];

        //Additions from Backend
        backendMap.forEach((annotation, id) => {
            if (!localMap.has(id)) {
                newFromParallelEditing.push({
                    type: ActionType.Add,
                    annotation: annotation,
                } as Action);
            }
        });

        // Deletions from Backend
        localMap.forEach((annotation, id) => {
            if (!backendMap.has(id)) {
                newFromParallelEditing.push({
                    type: ActionType.Remove,
                    annotation: annotation,
                } as Action);
            }
        });

        // Bbox Updates from Backend
        localMap.forEach((annotation, id) => {
            if (backendMap.has(id)) {
                const backendBBox = backendMap.get(id).bbox;
                if (
                    Math.round(annotation.bbox.x) !==
                        Math.round(backendBBox.x) ||
                    Math.round(annotation.bbox.y) !==
                        Math.round(backendBBox.y) ||
                    Math.round(annotation.bbox.width) !==
                        Math.round(backendBBox.width) ||
                    Math.round(annotation.bbox.height) !==
                        Math.round(backendBBox.height) ||
                    annotation.categoryId !== backendMap.get(id).categoryId
                ) {
                    newFromParallelEditing.push({
                        type: ActionType.Update,
                        annotation: backendMap.get(id),
                        originalAnnotation: annotation,
                    } as Action);
                }
            }
        });

        if (newFromParallelEditing.length > 0) {
            const messageData: MessageData = {
                type: MessageType.SUCCESS,
                message: {
                    text: 'Loaded Annotation Changes from Source',
                },
            };
            this.messageService.displayMessage(messageData);
        }
    }

    private restoreActiveFlag(visualAnnotations: ImgAnnotation[]) {
        // Reset Active Flag
        visualAnnotations.forEach((annotation: ImgAnnotation) => {
            if (annotation.id === this.selectedAnnotation?.id) {
                annotation.active = true;
                this.selectedAnnotation = annotation;
            }
            return annotation;
        });
    }

    private dontInterruptBboxModifying(
        visualAnnotations: ImgAnnotation[]
    ): Action[] {
        /**
         * Adds the current state of the selected Annotation to the visual Annotations, if it is currently moved around.
         * @param(visualAnnotations) ImgAnnotations as they are returned from the Backend
         * @returns {Action[]}: The past Actions for the selected Annotation (so they won't get applied immediately after)
         */
        if (this.selectedAnnotation && this.bboxCurrentlyModifiable) {
            const selectedAnnotationIdx: number = visualAnnotations.findIndex(
                (annotation: ImgAnnotation) => {
                    return annotation.id === this.selectedAnnotation.id;
                }
            );
            if (selectedAnnotationIdx !== -1) {
                visualAnnotations[selectedAnnotationIdx] =
                    this.selectedAnnotation;
                const selectedAnnotationActions: Action[] = this.commands.get(
                    this.selectedAnnotation.id
                );
                if (selectedAnnotationActions) {
                    // don't merge selectedAnnotation commands, just save them
                    this.commands.delete(this.selectedAnnotation.id);
                    return selectedAnnotationActions;
                }
            }
        }
        return null;
    }

    private updateVisualAnnotationsAndCheckIfCommandAlreadyExecuted(
        visualAnnotations: ImgAnnotation[],
        actionToCheck: Action
    ): void {
        const annotationToCheck: ImgAnnotation = this.deepCopyImgAnnotation(
            actionToCheck.annotation
        );
        const currentAnnotationIdx: number = visualAnnotations.findIndex(
            (annotation: ImgAnnotation) => {
                return annotation.id === annotationToCheck.id;
            }
        );
        if (
            actionToCheck.type === ActionType.Update ||
            actionToCheck.type === ActionType.Add
        ) {
            // Already present (update)
            if (currentAnnotationIdx !== -1) {
                visualAnnotations[currentAnnotationIdx] = annotationToCheck;
            }
            // not present
            else {
                visualAnnotations.push(annotationToCheck);
            }
        } else {
            // present but should be removed
            if (currentAnnotationIdx !== -1) {
                visualAnnotations.splice(currentAnnotationIdx, 1);
            }
        }
    }

    private addAction(action: Action) {
        // Deep Copy so bbox stays the same
        const actionToAdd = this.deepCopyAction(action);
        actionToAdd.annotation.active = false;
        if (actionToAdd.originalAnnotation) {
            actionToAdd.originalAnnotation.active = false;
        }
        // Undo/Redo Verhalten wie in Powerpoint
        this.actions = this.actions.slice(this.actionStateIndex + 1);
        this.actions.splice(0, 0, actionToAdd);
        this.actionStateIndex = -1;

        if (this.actions.length > this.maxActionsLimit) {
            this.actions.splice(this.maxActionsLimit);
        }
    }

    public deepCopyImgAnnotation(annotationToCopy: ImgAnnotation) {
        if (!annotationToCopy) {
            throw new Error(
                `No Annotation to copy. Annotation was ${annotationToCopy}`
            );
        }
        const copiedAnnotation = new ImgAnnotation(
            annotationToCopy.id,
            annotationToCopy.imageId,
            annotationToCopy.categoryId,
            annotationToCopy.bbox.x,
            annotationToCopy.bbox.y,
            annotationToCopy.bbox.width,
            annotationToCopy.bbox.height,
            annotationToCopy.polygon
        );
        copiedAnnotation.active = annotationToCopy.active;
        return copiedAnnotation;
    }

    public deepCopyAction(actionToCopy: Action) {
        return {
            type: actionToCopy.type,
            annotation: this.deepCopyImgAnnotation(actionToCopy.annotation),
            originalAnnotation: actionToCopy.originalAnnotation
                ? this.deepCopyImgAnnotation(actionToCopy.originalAnnotation)
                : null,
        } as Action;
    }

    private getAnnotationIndex(annotations, id) {
        return annotations.findIndex((annotation) => annotation.id === id);
    }

    public changedAnnotationState(): boolean {
        return this.commands.size > 0;
    }

    public unselectImg() {
        this.selectedImgId = null;
    }
}
